/**
 * HYPSEYA — Therapist Dashboard (v2 — Clinical)
 * Full clinical view: risk alerts, patient list with PHQ-9/GAD-7,
 * clinical notes, program assignment, inactivity flags.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth0 as useAuth0Real } from '@auth0/auth0-react';
import { useAuth0 as useAuth0Mock } from '@/MockAuth0Provider';
import orbUrl from '../orbAsset';
import ConfirmModal from '@/components/ConfirmModal';

const useAuth0 = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true' ? useAuth0Mock : useAuth0Real;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEAL = '#1E6B68';
const DARK = '#1E3A39';
const CREAM = '#F7F3EC';

const PROGRAMS_META = {
  grief:       { label: 'Grief & Loss',        color: '#4A7B9D' },
  anxiety:     { label: 'Anxiety & Overwhelm', color: '#2D8B87' },
  burnout:     { label: 'Burnout Recovery',    color: '#B8906A' },
  integration: { label: 'Trauma Integration',  color: '#7B68A0' },
  redesign:    { label: 'Redesign Your Mind',  color: '#1E6B68' },
};

// ── Severity helpers ──────────────────────────────────────────────────────────
const SEVERITY = {
  critical: { bg: 'rgba(192,57,43,0.1)',  border: '#C0392B', dot: '#C0392B', label: 'Critical'  },
  high:     { bg: 'rgba(224,123,74,0.1)', border: '#E07B4A', dot: '#E07B4A', label: 'High'      },
  medium:   { bg: 'rgba(184,144,106,0.1)',border: '#B8906A', dot: '#B8906A', label: 'Medium'    },
  low:      { bg: 'rgba(30,107,104,0.08)',border: '#2D8B87', dot: '#4AACA8', label: 'Low'       },
};

function epdsColor(s) {
  if (s == null) return '#AAB8B6';
  if (s >= 13) return '#D4756A';
  if (s >= 10) return '#C9A86C';
  return '#7EC8C8';
}
function phq9Color(s) {
  if (s == null) return '#AAB8B6';
  if (s >= 20) return '#C0392B';
  if (s >= 15) return '#E07B4A';
  if (s >= 10) return '#B8906A';
  if (s >= 5)  return '#4AACA8';
  return '#1E6B68';
}
function gad7Color(s) {
  if (s == null) return '#AAB8B6';
  if (s >= 15) return '#C0392B';
  if (s >= 10) return '#E07B4A';
  if (s >= 5)  return '#B8906A';
  return '#1E6B68';
}
function phq9Label(s) {
  if (s == null) return '—';
  if (s >= 20) return 'Severe';
  if (s >= 15) return 'Mod.Severe';
  if (s >= 10) return 'Moderate';
  if (s >= 5)  return 'Mild';
  return 'Minimal';
}
function gad7Label(s) {
  if (s == null) return '—';
  if (s >= 15) return 'Severe';
  if (s >= 10) return 'Moderate';
  if (s >= 5)  return 'Mild';
  return 'Minimal';
}
function daysAgoLabel(d) {
  if (d == null) return 'Never';
  if (d === 0) return 'Today';
  if (d === 1) return 'Yesterday';
  return `${d}d ago`;
}

// ── ERIBadge ─────────────────────────────────────────────────────────────────
function ERIBadge({ score }) {
  if (score == null) return <span style={{ fontSize: '0.75rem', color: '#C5D4D2' }}>—</span>;
  const color = score >= 80 ? TEAL : score >= 60 ? '#2D8B87' : score >= 40 ? '#4AACA8' : '#B8906A';
  const band  = score >= 80 ? 'Strong' : score >= 60 ? 'Growing' : score >= 40 ? 'Emerging' : 'Early';
  return (
    <div style={{ textAlign: 'right' }}>
      <span style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.4rem', fontWeight: '700', color }}>{Math.round(score)}</span>
      <div style={{ fontSize: '0.62rem', background: `${color}18`, color, padding: '2px 8px', borderRadius: 10, fontWeight: '600', display: 'inline-block', marginLeft: 6 }}>{band}</div>
    </div>
  );
}

// ── Risk Alert Banner ─────────────────────────────────────────────────────────
function RiskAlertBanner({ alert, therapistId, onAcknowledged }) {
  const sv = SEVERITY[alert.severity] || SEVERITY.low;
  const [acking, setAcking] = useState(false);
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(false);

  const ack = async () => {
    setAcking(true);
    try {
      await axios.post(`${API}/therapist/risk-alerts/${alert.id}/acknowledge`, { note });
      onAcknowledged(alert.id);
      toast.success('Alert acknowledged');
    } catch { toast.error('Failed to acknowledge'); }
    setAcking(false);
  };

  return (
    <div style={{
      background: sv.bg, border: `1.5px solid ${sv.border}`,
      borderRadius: 14, padding: '14px 16px', marginBottom: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: sv.dot, flexShrink: 0, marginTop: 5 }} />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
            <span style={{ fontSize: '0.82rem', fontWeight: '700', color: sv.dot }}>{sv.label}</span>
            <span style={{ fontSize: '0.78rem', fontWeight: '600', color: DARK }}>{alert.title}</span>
            {alert.patient_name && (
              <span style={{ fontSize: '0.72rem', color: '#8FA3A0' }}>· {alert.patient_name}</span>
            )}
          </div>
          <p style={{ margin: 0, fontSize: '0.78rem', color: '#4A6060', lineHeight: 1.5 }}>{alert.body}</p>
          {showNote && (
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Optional acknowledgement note…"
              style={{ width: '100%', marginTop: 8, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(184,211,207,0.5)', fontSize: '0.78rem', resize: 'none', height: 60, boxSizing: 'border-box', fontFamily: 'inherit', background: 'rgba(255,255,255,0.8)' }}
            />
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button onClick={() => setShowNote(s => !s)} style={{ padding: '5px 10px', background: 'transparent', border: `1px solid ${sv.border}`, borderRadius: 8, fontSize: '0.72rem', color: sv.dot, cursor: 'pointer' }}>
            {showNote ? 'Hide' : 'Note'}
          </button>
          <button onClick={ack} disabled={acking} style={{ padding: '5px 10px', background: sv.dot, border: 'none', borderRadius: 8, fontSize: '0.72rem', color: '#fff', cursor: 'pointer', fontWeight: '600' }}>
            {acking ? '…' : 'Ack ✓'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── MBC (measurement-based care) chip ────────────────────────────────────────
const MBC_META = {
  remission:   { label: 'Remission',   color: '#1E6B68', bg: 'rgba(30,107,104,0.1)'  },
  response:    { label: 'Response',    color: '#2D8B87', bg: 'rgba(45,139,135,0.1)'  },
  improving:   { label: 'Improving',   color: '#4AACA8', bg: 'rgba(74,172,168,0.1)'  },
  monitoring:  { label: 'Monitoring',  color: '#B8906A', bg: 'rgba(184,144,106,0.1)' },
  no_response: { label: 'No response', color: '#C0392B', bg: 'rgba(192,57,43,0.1)'   },
};

function MBCChips({ patientId }) {
  const [mbc, setMbc] = useState(null);

  useEffect(() => {
    axios.get(`${API}/mbc/status/${patientId}`)
      .then(r => setMbc(r.data))
      .catch(() => setMbc({ available: false }));
  }, [patientId]);

  if (!mbc?.available) return null;

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '0.6rem', color: '#AAB8B6', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase' }}>MBC</span>
        {['phq9', 'gad7'].map(scale => {
          const s = mbc[scale];
          const meta = MBC_META[s.status] || MBC_META.monitoring;
          return (
            <span key={scale} style={{
              fontSize: '0.66rem', fontWeight: '700', color: meta.color, background: meta.bg,
              padding: '3px 10px', borderRadius: 10,
            }}>
              {scale.toUpperCase()}: {meta.label} {s.pct_change !== 0 && `(${s.pct_change > 0 ? '+' : ''}${s.pct_change}%)`}
            </span>
          );
        })}
      </div>
      {mbc.any_adjustment_flag && (
        <p style={{ margin: '6px 0 0', fontSize: '0.7rem', color: '#C0392B', fontWeight: '600' }}>
          ⚑ No response after {mbc.weeks_tracked} weeks — consider treatment adjustment
        </p>
      )}
    </div>
  );
}

// ── Pre-Session Brief Modal ───────────────────────────────────────────────────
function PreSessionBriefModal({ patient, therapistId, onClose }) {
  const [brief, setBrief] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/therapist/pre-session-brief/${patient.patient_id}`, { params: { therapist_id: therapistId } })
      .then(r => setBrief(r.data))
      .catch(() => setError(true));
  }, [patient.patient_id, therapistId]);

  const row = (label, value, color) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid rgba(184,211,207,0.25)' }}>
      <span style={{ fontSize: '0.8rem', color: '#8FA3A0' }}>{label}</span>
      <span style={{ fontSize: '0.82rem', fontWeight: '700', color: color || DARK }}>{value}</span>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9500, background: 'rgba(10,26,25,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto', background: '#fff', borderRadius: 20, padding: '24px 24px 28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.25rem', color: DARK, margin: 0 }}>Pre-Session Brief</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: '1.1rem', color: '#AAB8B6', cursor: 'pointer' }}>✕</button>
        </div>
        <p style={{ margin: '0 0 16px', fontSize: '0.8rem', color: '#8FA3A0' }}>
          {patient.display_name || patient.patient_email}
          {brief && ` · last ${brief.days_since_last_session} days`}
        </p>

        {error && <p style={{ fontSize: '0.85rem', color: '#C0392B' }}>Could not generate the brief.</p>}
        {!brief && !error && <p style={{ fontSize: '0.85rem', color: '#AAB8B6', textAlign: 'center', padding: '20px 0' }}>Generating…</p>}

        {brief && (
          <>
            {/* ERI delta */}
            <div style={{ background: 'rgba(30,107,104,0.05)', borderRadius: 14, padding: '14px 16px', marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: '0.72rem', color: '#8FA3A0', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em' }}>ERI since last session</span>
                {brief.eri.delta != null ? (
                  <span style={{ fontSize: '1.15rem', fontWeight: '700', color: brief.eri.delta >= 0 ? TEAL : '#C0392B' }}>
                    {brief.eri.delta >= 0 ? '+' : ''}{brief.eri.delta}
                  </span>
                ) : <span style={{ fontSize: '0.85rem', color: '#AAB8B6' }}>—</span>}
              </div>
              <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#8FA3A0' }}>
                {brief.eri.at_last_session != null ? `${Math.round(brief.eri.at_last_session)} → ` : ''}
                {brief.eri.current != null ? Math.round(brief.eri.current) : 'no data'}
              </p>
            </div>

            {row('Adherence', `${brief.adherence.days_practised}/${brief.adherence.days_elapsed} days (${brief.adherence.pct}%)`,
              brief.adherence.pct >= 60 ? TEAL : '#B8906A')}
            {brief.latest_assessment && row('PHQ-9 / GAD-7',
              `${brief.latest_assessment.phq9_score} / ${brief.latest_assessment.gad7_score}`,
              phq9Color(brief.latest_assessment.phq9_score))}
            {brief.somatic_avg_last7 != null && row('Somatic avg (7 check-ins)', `${brief.somatic_avg_last7}/5`)}
            {row('Journal entries', brief.journal_entries_since)}
            {brief.sos_events_since > 0 && row('SOS moments', brief.sos_events_since, '#C0392B')}
            {brief.open_alerts.length > 0 && row('Open alerts', brief.open_alerts.length, '#C0392B')}

            {/* MBC summary */}
            {brief.mbc?.available && (
              <div style={{ marginTop: 12 }}>
                <MBCChips patientId={patient.patient_id} />
              </div>
            )}

            {/* Psychedelic protocol status */}
            {(brief.integration_protocol || brief.preparation_protocol) && (
              <div style={{ marginTop: 12, background: 'rgba(107,84,139,0.06)', border: '1px solid rgba(107,84,139,0.2)', borderRadius: 12, padding: '12px 14px' }}>
                <p style={{ margin: 0, fontSize: '0.78rem', color: '#6B548B', fontWeight: '600' }}>
                  {brief.integration_protocol
                    ? `Integration protocol active · ${brief.integration_protocol.ceremony_type} (${brief.integration_protocol.ceremony_date})`
                    : `Preparing for ceremony · ${brief.preparation_protocol.ceremony_type} (${brief.preparation_protocol.ceremony_date})`}
                </p>
                {brief.meq30_latest && (
                  <p style={{ margin: '6px 0 0', fontSize: '0.74rem', color: '#8FA3A0' }}>
                    MEQ-30: {brief.meq30_latest.total_pct}%
                    {brief.meq30_latest.complete_mystical_experience && ' · complete mystical experience'}
                    {brief.ebi_latest && ` · EBI: ${brief.ebi_latest.ebi_score}`}
                  </p>
                )}
              </div>
            )}

            <p style={{ margin: '16px 0 0', fontSize: '0.68rem', color: '#AAB8B6', lineHeight: 1.5 }}>
              {brief.privacy_note}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ── Clinical Notes Panel ──────────────────────────────────────────────────────
function ClinicalNotesPanel({ patient, therapistId }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!loaded) {
      axios.get(`${API}/therapist/clinical-notes/${patient.patient_id}`, { params: { therapist_id: therapistId } })
        .then(r => { setNotes(r.data.notes || []); setLoaded(true); })
        .catch(() => setLoaded(true));
    }
  }, [loaded, patient.patient_id, therapistId]);

  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/therapist/clinical-notes`, {
        therapist_id: therapistId,
        patient_id: patient.patient_id,
        text: text.trim(),
      });
      setNotes(prev => [res.data, ...prev]);
      setText('');
      toast.success('Note saved');
    } catch { toast.error('Failed to save note'); }
    setSaving(false);
  };

  return (
    <div style={{ marginTop: 14, borderTop: '1px solid rgba(184,211,207,0.3)', paddingTop: 12 }}>
      <div style={{ fontSize: '0.65rem', color: '#8FA3A0', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Clinical Notes</div>
      {notes.filter(n => n.type !== 'system').map(n => (
        <div key={n.id} style={{ background: 'rgba(247,243,236,0.8)', borderRadius: 8, padding: '8px 10px', marginBottom: 6 }}>
          <p style={{ margin: 0, fontSize: '0.78rem', color: DARK, lineHeight: 1.5 }}>{n.text}</p>
          <span style={{ fontSize: '0.65rem', color: '#AAB8B6' }}>{n.created_at?.slice(0, 10)}</span>
        </div>
      ))}
      {notes.filter(n => n.type === 'system').map(n => (
        <div key={n.id} style={{ borderLeft: '2px solid rgba(184,211,207,0.5)', paddingLeft: 8, marginBottom: 5 }}>
          <p style={{ margin: 0, fontSize: '0.7rem', color: '#AAB8B6', fontStyle: 'italic' }}>{n.text} · {n.created_at?.slice(0, 10)}</p>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <input
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && save()}
          placeholder="Add clinical note…"
          style={{ flex: 1, padding: '8px 12px', borderRadius: 10, border: '1px solid rgba(184,211,207,0.5)', fontSize: '0.78rem', background: 'rgba(255,255,255,0.9)', outline: 'none', fontFamily: 'inherit' }}
        />
        <button onClick={save} disabled={!text.trim() || saving} style={{ padding: '8px 14px', background: text.trim() ? TEAL : 'rgba(184,211,207,0.3)', color: text.trim() ? '#fff' : '#AAB8B6', border: 'none', borderRadius: 10, fontSize: '0.78rem', fontWeight: '600', cursor: text.trim() ? 'pointer' : 'default' }}>
          {saving ? '…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ── Assign Program Modal ──────────────────────────────────────────────────────
function AssignProgramModal({ patient, therapistId, onDone, onClose }) {
  const [selected, setSelected] = useState('');
  const [assigning, setAssigning] = useState(false);

  const assign = async () => {
    if (!selected) return;
    setAssigning(true);
    try {
      await axios.post(`${API}/therapist/assign-program`, {
        therapist_id: therapistId,
        patient_id: patient.patient_id,
        program_id: selected,
      });
      toast.success(`${PROGRAMS_META[selected]?.label} assigned`);
      onDone();
    } catch { toast.error('Failed to assign program'); }
    setAssigning(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(10,26,25,0.75)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
      <div style={{ width: '100%', maxWidth: 520, background: `linear-gradient(160deg, ${CREAM} 0%, #EFE7DD 100%)`, borderRadius: '24px 24px 0 0', padding: '28px 24px 40px', animation: 'hyp-slide-up 0.3s ease' }}>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.3rem', color: DARK, margin: '0 0 6px' }}>Assign Program</h2>
        <p style={{ margin: '0 0 20px', fontSize: '0.82rem', color: '#8FA3A0' }}>
          Assign a structured 8-week program to <strong>{patient.display_name || patient.patient_email}</strong>
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
          {Object.entries(PROGRAMS_META).map(([id, m]) => (
            <button key={id} onClick={() => setSelected(id)} style={{
              padding: '12px 16px', textAlign: 'left', borderRadius: 12,
              background: selected === id ? `${m.color}15` : 'rgba(255,255,255,0.8)',
              border: `1.5px solid ${selected === id ? m.color : 'rgba(184,211,207,0.4)'}`,
              cursor: 'pointer', fontSize: '0.88rem', fontWeight: selected === id ? '700' : '500',
              color: selected === id ? m.color : DARK, transition: 'all 0.15s',
            }}>
              {m.label}
              {patient.active_program?.program_id === id && (
                <span style={{ fontSize: '0.7rem', marginLeft: 8, color: m.color, fontWeight: '600' }}>· currently active</span>
              )}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={assign} disabled={!selected || assigning} style={{ flex: 1, padding: '14px', background: selected ? `linear-gradient(135deg, ${TEAL}, #2D8B87)` : 'rgba(184,211,207,0.3)', color: selected ? '#fff' : '#AAB8B6', border: 'none', borderRadius: 20, fontWeight: '600', cursor: selected ? 'pointer' : 'default' }}>
            {assigning ? 'Assigning…' : 'Assign program →'}
          </button>
          <button onClick={onClose} style={{ padding: '14px 18px', background: 'transparent', border: '1px solid rgba(184,211,207,0.5)', borderRadius: 20, color: '#AAB8B6', cursor: 'pointer' }}>Cancel</button>
        </div>
      </div>
      <style>{`@keyframes hyp-slide-up { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </div>
  );
}

// ── Patient Card ──────────────────────────────────────────────────────────────
function PatientCard({ patient, therapistId, onRevoke, onAssignProgram, onPrescribe }) {
  const [expanded, setExpanded] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [showBrief, setShowBrief] = useState(false);
  const isPending = patient.status === 'pending';
  const riskSv = patient.risk_level ? SEVERITY[patient.risk_level] : null;
  const isInactive = patient.days_inactive != null && patient.days_inactive >= 7;

  const viewReport = async () => {
    setLoadingReport(true);
    try {
      const res = await axios.post(`${API}/share/report/${patient.patient_id}`);
      window.open(`/therapist-report/${res.data.token}`, '_blank');
    } catch { toast.error('Could not load patient report'); }
    setLoadingReport(false);
  };

  const downloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const res = await axios.get(`${API}/reports/clinical-pdf/${patient.patient_id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hypseya_${(patient.display_name || 'patient').replace(/\s/g, '_').toLowerCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('PDF generation failed. Check backend logs.'); }
    setDownloadingPDF(false);
  };

  return (
    <div style={{
      background: '#fff', borderRadius: 18, marginBottom: 10,
      boxShadow: riskSv ? `0 2px 20px ${riskSv.dot}20` : '0 2px 14px rgba(30,107,104,0.06)',
      border: riskSv ? `1.5px solid ${riskSv.border}` : '1px solid rgba(184,211,207,0.2)',
      opacity: isPending ? 0.75 : 1,
      overflow: 'hidden',
    }}>
      {/* Risk strip */}
      {riskSv && (
        <div style={{ height: 3, background: `linear-gradient(90deg, ${riskSv.dot}, transparent)` }} />
      )}

      <div style={{ padding: '18px 20px' }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
          {/* Avatar */}
          <div style={{
            width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
            background: riskSv ? `${riskSv.dot}18` : 'linear-gradient(135deg, rgba(30,107,104,0.12), rgba(45,139,135,0.08))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1rem', fontWeight: '700', color: riskSv?.dot || TEAL,
          }}>
            {(patient.display_name || patient.patient_email || '?').charAt(0).toUpperCase()}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 2 }}>
              <span style={{ fontSize: '0.95rem', fontWeight: '700', color: DARK }}>
                {isPending ? patient.patient_email : (patient.display_name || patient.patient_email)}
              </span>
              {isPending && <span style={{ fontSize: '0.62rem', background: 'rgba(184,144,106,0.15)', color: '#B8906A', padding: '2px 8px', borderRadius: 10, fontWeight: '600' }}>Pending</span>}
              {riskSv && <span style={{ fontSize: '0.62rem', background: riskSv.bg, color: riskSv.dot, padding: '2px 8px', borderRadius: 10, fontWeight: '700', border: `1px solid ${riskSv.border}` }}>⚠ {riskSv.label}</span>}
              {isInactive && !riskSv && <span style={{ fontSize: '0.62rem', background: 'rgba(184,144,106,0.1)', color: '#B8906A', padding: '2px 8px', borderRadius: 10, fontWeight: '600' }}>Inactive {patient.days_inactive}d</span>}
            </div>
            <div style={{ fontSize: '0.72rem', color: '#AAB8B6' }}>
              {isPending ? 'Invite sent · awaiting acceptance' : patient.patient_email}
            </div>
          </div>

          {!isPending && <ERIBadge score={patient.eri_score} />}
        </div>

        {!isPending && (
          <>
            {/* PHQ-9 + GAD-7 (standard) or EPDS (matrescence) */}
            {patient.mode === 'matrescence' ? (
              <div style={{ marginBottom: 10 }}>
                {/* EPDS score */}
                <div style={{ background: 'rgba(196,134,106,0.07)', border: '1px solid rgba(196,134,106,0.18)', borderRadius: 10, padding: '10px 12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, alignItems: 'baseline' }}>
                    <span style={{ fontSize: '0.68rem', color: '#C4866A', fontWeight: '600' }}>EPDS (Postnatal)</span>
                    <span style={{ fontSize: '0.88rem', fontWeight: '700', color: epdsColor(patient.epds_score) }}>
                      {patient.epds_score ?? '—'}<span style={{ fontSize: '0.6rem', color: '#aaa' }}>/30</span>
                    </span>
                  </div>
                  <div style={{ height: 4, background: '#E8E8E8', borderRadius: 3 }}>
                    {patient.epds_score != null && (
                      <div style={{ height: '100%', width: `${(patient.epds_score / 30) * 100}%`, background: epdsColor(patient.epds_score), borderRadius: 3, transition: 'width 0.4s ease' }} />
                    )}
                  </div>
                  <span style={{ fontSize: '0.68rem', color: epdsColor(patient.epds_score), fontWeight: '600', marginTop: 3, display: 'block', textTransform: 'capitalize' }}>
                    {patient.epds_severity ?? 'No data'}
                    {patient.epds_date && <span style={{ color: '#aaa', fontWeight: 400, marginLeft: 8 }}>{daysAgoLabel(Math.floor((Date.now() - new Date(patient.epds_date)) / 86400000))}</span>}
                  </span>
                </div>
                <div style={{ fontSize: '0.65rem', color: '#C4866A', marginTop: 5, opacity: 0.65, fontStyle: 'italic' }}>
                  Matrescence mode · Edinburgh Postnatal Depression Scale
                </div>
              </div>
            ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
              {[
                { label: 'PHQ-9', score: patient.phq9_score, max: 27, color: phq9Color(patient.phq9_score), sev: phq9Label(patient.phq9_score) },
                { label: 'GAD-7', score: patient.gad7_score, max: 21, color: gad7Color(patient.gad7_score), sev: gad7Label(patient.gad7_score) },
              ].map(item => (
                <div key={item.label} style={{ background: '#F8F9F8', borderRadius: 10, padding: '10px 12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, alignItems: 'baseline' }}>
                    <span style={{ fontSize: '0.68rem', color: '#888', fontWeight: '600' }}>{item.label}</span>
                    <span style={{ fontSize: '0.88rem', fontWeight: '700', color: item.color }}>{item.score ?? '—'}<span style={{ fontSize: '0.6rem', color: '#aaa' }}>/{item.max}</span></span>
                  </div>
                  <div style={{ height: 4, background: '#E8E8E8', borderRadius: 3 }}>
                    {item.score != null && <div style={{ height: '100%', width: `${(item.score / item.max) * 100}%`, background: item.color, borderRadius: 3, transition: 'width 0.4s ease' }} />}
                  </div>
                  <span style={{ fontSize: '0.68rem', color: item.color, fontWeight: '600', marginTop: 3, display: 'block' }}>{item.sev}</span>
                </div>
              ))}
            </div>
            )}

            {/* MBC status chips */}
            <MBCChips patientId={patient.patient_id} />

            {/* Session stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 10 }}>
              {[
                { label: 'Sessions', value: patient.total_sessions ?? 0 },
                { label: 'This week', value: patient.sessions_last_7_days ?? 0 },
                { label: 'Last active', value: daysAgoLabel(patient.days_inactive) },
              ].map(s => (
                <div key={s.label} style={{ background: 'rgba(247,243,236,0.8)', borderRadius: 10, padding: '8px 6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.58rem', color: '#AAB8B6', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 2 }}>{s.label}</div>
                  <div style={{ fontSize: '0.85rem', fontWeight: '700', color: TEAL }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* ERI sub-scores */}
            {patient.regulation_capacity != null && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                {[
                  { label: 'Regulation',   value: patient.regulation_capacity },
                  { label: 'Stability',    value: patient.stability_score },
                  { label: 'Consistency',  value: patient.practice_consistency },
                ].map(d => (
                  <div key={d.label} style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', color: '#AAB8B6', marginBottom: 3 }}>
                      <span>{d.label}</span><span>{Math.round(d.value ?? 0)}</span>
                    </div>
                    <div style={{ height: 3, background: 'rgba(184,211,207,0.3)', borderRadius: 2 }}>
                      <div style={{ height: '100%', width: `${d.value ?? 0}%`, background: 'linear-gradient(90deg, #1E6B68, #4AACA8)', borderRadius: 2 }} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Active program badge */}
            {patient.active_program && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: PROGRAMS_META[patient.active_program.program_id]?.color || TEAL }} />
                <span style={{ fontSize: '0.72rem', color: '#6B8B87', fontWeight: '600' }}>
                  {patient.active_program.title}
                </span>
                <span style={{ fontSize: '0.65rem', color: '#AAB8B6' }}>· enrolled {patient.active_program.enrolled_at?.slice(0, 10)}</span>
              </div>
            )}

            {/* Expanded: Clinical Notes */}
            {expanded && <ClinicalNotesPanel patient={patient} therapistId={therapistId} />}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <button onClick={() => setShowBrief(true)} style={{ flex: 1, minWidth: 80, padding: '9px', background: `linear-gradient(135deg, ${TEAL}12, rgba(45,139,135,0.14))`, border: `1px solid ${TEAL}40`, borderRadius: 12, color: TEAL, fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer' }}>
                ☰ Brief
              </button>
              <button onClick={viewReport} disabled={loadingReport} style={{ flex: 1, minWidth: 80, padding: '9px', background: 'rgba(30,107,104,0.08)', border: '1px solid rgba(30,107,104,0.2)', borderRadius: 12, color: TEAL, fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer', opacity: loadingReport ? 0.6 : 1 }}>
                {loadingReport ? '…' : 'Report →'}
              </button>
              <button onClick={downloadPDF} disabled={downloadingPDF} style={{ flex: 1, minWidth: 80, padding: '9px', background: 'rgba(30,107,104,0.06)', border: '1px solid rgba(30,107,104,0.15)', borderRadius: 12, color: TEAL, fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer', opacity: downloadingPDF ? 0.6 : 1 }}>
                {downloadingPDF ? '…' : '↓ PDF'}
              </button>
              <button onClick={() => onAssignProgram(patient)} style={{ flex: 1, minWidth: 80, padding: '9px', background: 'rgba(184,144,106,0.08)', border: '1px solid rgba(184,144,106,0.2)', borderRadius: 12, color: '#B8906A', fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer' }}>
                Program
              </button>
              <button onClick={() => onPrescribe(patient)} style={{ flex: 1, minWidth: 80, padding: '9px', background: 'rgba(30,107,104,0.06)', border: '1px solid rgba(30,107,104,0.2)', borderRadius: 12, color: TEAL, fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer' }}>
                Prescribe
              </button>
              <button onClick={() => setExpanded(e => !e)} style={{ padding: '9px 12px', background: expanded ? 'rgba(30,107,104,0.1)' : 'transparent', border: '1px solid rgba(184,211,207,0.4)', borderRadius: 12, color: '#8FA3A0', fontSize: '0.78rem', cursor: 'pointer' }}>
                {expanded ? '▲' : '▼ Notes'}
              </button>
            </div>
          </>
        )}

        {/* Revoke (pending or active) */}
        {!expanded && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: isPending ? 12 : 0 }}>
            <button onClick={() => onRevoke(patient.connection_id)} style={{ padding: '5px 12px', background: 'transparent', border: '1px solid rgba(184,211,207,0.4)', borderRadius: 8, color: '#C5D4D2', fontSize: '0.7rem', cursor: 'pointer' }}>
              Remove
            </button>
          </div>
        )}
      </div>

      {showBrief && (
        <PreSessionBriefModal patient={patient} therapistId={therapistId} onClose={() => setShowBrief(false)} />
      )}
    </div>
  );
}

// ── Prescribe Modal ───────────────────────────────────────────────────────────
function PrescribeModal({ therapist, patient, onDone, onClose }) {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [expires, setExpires] = useState(7);
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!title.trim() || !message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/therapist/prescribe`, {
        therapist_id: therapist.user_id,
        therapist_name: therapist.name || 'Your therapist',
        patient_user_id: patient.patient_id,
        title: title.trim(),
        message: message.trim(),
        type: 'note',
        expires_in_days: expires,
      });
      toast.success(`Prescription sent to ${patient.display_name || patient.patient_email}`);
      onDone();
    } catch {
      toast.error('Could not send prescription.');
    }
    setSending(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: '24px 24px 0 0', padding: '28px 24px 40px', width: '100%', maxWidth: '520px', animation: 'slideUp 0.28s ease' }}>
        <style>{`@keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
        <div style={{ width: 36, height: 4, background: 'rgba(184,211,207,0.6)', borderRadius: 2, margin: '0 auto 20px' }} />
        <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.15rem', color: '#1E3A3A', margin: '0 0 6px' }}>
          Prescription
        </h3>
        <p style={{ fontSize: '0.8rem', color: '#9AADAB', margin: '0 0 20px' }}>
          For {patient.display_name || patient.patient_email} · visible on their home screen
        </p>

        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#6A8F8C', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Title</label>
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="e.g. Breathing for Sunday evenings"
          style={{ width: '100%', border: '1.5px solid rgba(184,211,207,0.5)', borderRadius: '10px', padding: '10px 12px', fontSize: '0.9rem', color: '#1E3A3A', outline: 'none', marginBottom: '14px', boxSizing: 'border-box' }}
        />

        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#6A8F8C', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Message</label>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="What do you recommend, and why…"
          rows={4}
          style={{ width: '100%', border: '1.5px solid rgba(184,211,207,0.5)', borderRadius: '10px', padding: '10px 12px', fontSize: '0.88rem', color: '#1E3A3A', outline: 'none', resize: 'none', marginBottom: '14px', boxSizing: 'border-box' }}
        />

        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#6A8F8C', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Expires in</label>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '22px' }}>
          {[7, 14, 30].map(d => (
            <button key={d} onClick={() => setExpires(d)} style={{
              flex: 1, padding: '8px', borderRadius: '10px',
              background: expires === d ? 'rgba(30,107,104,0.1)' : 'transparent',
              border: expires === d ? '1.5px solid rgba(30,107,104,0.3)' : '1px solid rgba(184,211,207,0.5)',
              color: expires === d ? TEAL : '#9AADAB', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer',
            }}>
              {d} days
            </button>
          ))}
        </div>

        <button onClick={submit} disabled={sending || !title.trim() || !message.trim()} style={{
          width: '100%', padding: '14px', borderRadius: '14px',
          background: 'linear-gradient(135deg, #1E6B68, #2D8B87)',
          border: 'none', color: '#fff', fontSize: '0.95rem', fontWeight: '600',
          cursor: 'pointer', opacity: sending ? 0.7 : 1,
        }}>
          {sending ? 'Sending…' : 'Send prescription'}
        </button>
      </div>
    </div>
  );
}

// ── Invite Modal ──────────────────────────────────────────────────────────────
function InviteModal({ therapistId, onDone, onClose }) {
  const { getAccessTokenSilently } = useAuth0();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  const handleInvite = async () => {
    if (!email.trim()) return;
    setSending(true);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      await axios.post(`${API}/therapist/invite-patient`, { therapist_id: therapistId, patient_email: email.trim() }, cfg);
      toast.success(`Invite created for ${email}`);
      onDone();
    } catch { toast.error('Failed to create invite.'); }
    setSending(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 8000, background: 'rgba(10,26,25,0.75)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
      <div style={{ width: '100%', maxWidth: 520, background: `linear-gradient(160deg, ${CREAM} 0%, #EFE7DD 100%)`, borderRadius: '24px 24px 0 0', padding: '28px 24px 40px', animation: 'hyp-slide-up 0.35s ease' }}>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.35rem', color: DARK, margin: '0 0 6px' }}>Invite Patient</h2>
        <p style={{ margin: '0 0 22px', fontSize: '0.82rem', color: '#8FA3A0', lineHeight: 1.6 }}>
          The patient will receive access to the protocol and their data will be shared with your dashboard.
        </p>
        <label style={{ display: 'block', fontSize: '0.78rem', color: '#8FA3A0', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>Patient email</label>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="patient@email.com" onKeyDown={e => e.key === 'Enter' && handleInvite()}
          style={{ width: '100%', padding: '14px 16px', marginBottom: 20, borderRadius: 14, border: '1px solid rgba(184,211,207,0.5)', background: 'rgba(255,255,255,0.8)', fontSize: '0.95rem', color: DARK, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
        />
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleInvite} disabled={!email.trim() || sending} style={{ flex: 1, padding: '14px', background: email.trim() ? `linear-gradient(135deg, ${TEAL} 0%, #2D8B87 100%)` : 'rgba(184,211,207,0.3)', color: email.trim() ? '#fff' : '#AAB8B6', border: 'none', borderRadius: 20, fontSize: '0.9rem', fontWeight: '600', cursor: email.trim() ? 'pointer' : 'default' }}>
            {sending ? 'Sending…' : 'Create invite →'}
          </button>
          <button onClick={onClose} style={{ padding: '14px 18px', background: 'transparent', border: '1px solid rgba(184,211,207,0.5)', borderRadius: 20, color: '#AAB8B6', cursor: 'pointer' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function TherapistDashboard() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [user, setUser] = useState(null);
  const [patients, setPatients] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [checkingInactivity, setCheckingInactivity] = useState(false);
  const [prescribeTarget, setPrescribeTarget] = useState(null);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) { try { setUser(JSON.parse(u)); } catch {} }
    setTimeout(() => setIsVisible(true), 60);
  }, []);

  const authCfg = useCallback(async () => {
    try { const token = await getAccessTokenSilently(); return { headers: { Authorization: `Bearer ${token}` } }; }
    catch { return {}; }
  }, [getAccessTokenSilently]);

  const loadPatients = useCallback(async () => {
    if (!user?.user_id) return;
    setLoading(true);
    try {
      const cfg = await authCfg();
      const [pRes, aRes] = await Promise.all([
        axios.get(`${API}/therapist/patients/${user.user_id}`, cfg),
        axios.get(`${API}/therapist/risk-alerts/${user.user_id}`, cfg),
      ]);
      setPatients(pRes.data);
      setAlerts(aRes.data.alerts || []);
    } catch {}
    setLoading(false);
  }, [user, authCfg]);

  useEffect(() => { if (user?.user_id) loadPatients(); }, [user, loadPatients]);

  const checkInactivity = async () => {
    setCheckingInactivity(true);
    try {
      const cfg = await authCfg();
      const res = await axios.get(`${API}/therapist/inactivity-check/${user.user_id}`, cfg);
      const inactive = res.data.inactive_patients || [];
      if (inactive.length > 0) {
        toast.warning(`${inactive.length} patient(s) inactive for 7+ days`);
        loadPatients();
      } else {
        toast.success('All patients active this week');
      }
    } catch { toast.error('Failed to check inactivity'); }
    setCheckingInactivity(false);
  };

  const handleRevoke = (connectionId) => {
    setConfirmModal({
      title: 'Remove patient connection?',
      message: 'This will disconnect you from this patient. They will no longer appear in your dashboard.',
      confirmLabel: 'Remove connection',
      destructive: true,
      onConfirm: async () => {
        try {
          const cfg = await authCfg();
          await axios.delete(`${API}/therapist/disconnect`, { ...cfg, params: { therapist_id: user.user_id, connection_id: connectionId } });
          toast.success('Connection removed.');
          setPatients(prev => prev.filter(p => p.connection_id !== connectionId));
        } catch { toast.error('Failed to remove connection.'); }
      },
    });
  };

  const handleAcknowledged = (alertId) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId));
  };

  const activePatients = patients.filter(p => p.status === 'active');
  const pendingPatients = patients.filter(p => p.status === 'pending');
  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const highCount = alerts.filter(a => a.severity === 'high' || a.severity === 'medium').length;
  const inactiveCount = activePatients.filter(p => p.days_inactive != null && p.days_inactive >= 7).length;

  return (
    <>
    {confirmModal && <ConfirmModal {...confirmModal} onClose={() => setConfirmModal(null)} />}
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 55%, rgba(184,211,207,0.15) 100%)',
      padding: '0 0 100px',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(8px)',
      transition: 'opacity 0.45s ease, transform 0.45s ease',
    }}>
      <div style={{ padding: '20px 20px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(184,211,207,0.4)', borderRadius: 20, padding: '8px 16px', cursor: 'pointer', fontSize: '0.82rem', color: '#8FA3A0' }}>← Back</button>
      </div>

      <div style={{ maxWidth: 560, margin: '0 auto', padding: '20px 20px 0' }}>

        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.7rem', color: DARK, margin: '0 0 4px', fontWeight: '500' }}>Clinical Dashboard</h1>
          <p style={{ margin: 0, color: '#8FA3A0', fontSize: '0.85rem' }}>HYPSEYA Therapist Portal</p>
        </div>

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
          {[
            { label: 'Patients',  value: activePatients.length,  color: TEAL      },
            { label: 'Critical',  value: criticalCount,           color: '#C0392B' },
            { label: 'Alerts',    value: highCount,               color: '#E07B4A' },
            { label: 'Inactive',  value: inactiveCount,           color: '#B8906A' },
          ].map(s => (
            <div key={s.label} style={{ background: '#fff', borderRadius: 14, padding: '12px 10px', boxShadow: '0 2px 10px rgba(30,107,104,0.05)', textAlign: 'center' }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.6rem', fontWeight: '700', color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '0.62rem', color: '#AAB8B6', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <button onClick={() => setShowInvite(true)} style={{ flex: 1, padding: '12px', background: `linear-gradient(135deg, ${TEAL} 0%, #2D8B87 100%)`, color: '#fff', border: 'none', borderRadius: 16, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 16px rgba(30,107,104,0.25)' }}>
            + Invite patient
          </button>
          <button onClick={checkInactivity} disabled={checkingInactivity} style={{ padding: '12px 16px', background: 'rgba(184,144,106,0.1)', border: '1px solid rgba(184,144,106,0.3)', borderRadius: 16, fontSize: '0.82rem', color: '#B8906A', fontWeight: '600', cursor: 'pointer', opacity: checkingInactivity ? 0.6 : 1 }}>
            {checkingInactivity ? '…' : '⏰ Check'}
          </button>
        </div>

        {/* Risk Alerts */}
        {alerts.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: '0.65rem', color: '#C0392B', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 }}>
              ⚠ Clinical Alerts ({alerts.length})
            </div>
            {alerts.map(alert => (
              <RiskAlertBanner key={alert.id} alert={alert} therapistId={user?.user_id} onAcknowledged={handleAcknowledged} />
            ))}
          </div>
        )}

        {/* Patient List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#AAB8B6' }}>Loading…</div>
        ) : patients.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 20, padding: '40px 24px', textAlign: 'center', boxShadow: '0 2px 16px rgba(30,107,104,0.06)' }}>
            <img src={orbUrl} alt="" style={{ width: 70, marginBottom: 16 }} />
            <h3 style={{ fontFamily: "'Playfair Display', serif", color: DARK, margin: '0 0 8px', fontSize: '1.2rem' }}>No patients yet</h3>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#AAB8B6', lineHeight: 1.6 }}>Invite your patients to start tracking their progress.</p>
          </div>
        ) : (
          <>
            {activePatients.length > 0 && (
              <>
                <div style={{ fontSize: '0.65rem', color: '#8FA3A0', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: '700', marginBottom: 10 }}>Active patients</div>
                {activePatients.map(p => (
                  <PatientCard key={p.connection_id} patient={p} therapistId={user?.user_id} onRevoke={handleRevoke} onAssignProgram={setAssignTarget} onPrescribe={setPrescribeTarget} />
                ))}
              </>
            )}
            {pendingPatients.length > 0 && (
              <>
                <div style={{ fontSize: '0.65rem', color: '#8FA3A0', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: '700', marginBottom: 10, marginTop: 20 }}>Pending invites</div>
                {pendingPatients.map(p => (
                  <PatientCard key={p.connection_id} patient={p} therapistId={user?.user_id} onRevoke={handleRevoke} onAssignProgram={setAssignTarget} onPrescribe={setPrescribeTarget} />
                ))}
              </>
            )}
          </>
        )}

        {/* Disclaimer */}
        <div style={{ marginTop: 20, padding: '14px 18px', background: 'rgba(30,107,104,0.05)', border: '1px solid rgba(30,107,104,0.12)', borderRadius: 14, fontSize: '0.75rem', color: '#8FA3A0', lineHeight: 1.6 }}>
          Patient data is shared with explicit mutual consent. PHQ-9 and GAD-7 scores are self-reported — clinical judgement remains with the licensed practitioner. All alerts are informational and do not replace direct clinical assessment.
        </div>
      </div>

      {showInvite && user?.user_id && (
        <InviteModal therapistId={user.user_id} onDone={() => { setShowInvite(false); loadPatients(); }} onClose={() => setShowInvite(false)} />
      )}
      {assignTarget && user?.user_id && (
        <AssignProgramModal patient={assignTarget} therapistId={user.user_id} onDone={() => { setAssignTarget(null); loadPatients(); }} onClose={() => setAssignTarget(null)} />
      )}
      {prescribeTarget && user && (
        <PrescribeModal therapist={user} patient={prescribeTarget} onDone={() => setPrescribeTarget(null)} onClose={() => setPrescribeTarget(null)} />
      )}
    </div>
    </>
  );
}
